import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarBlank, Download, HandCoins, Money, Clock, ArrowSquareOut } from "@phosphor-icons/react";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const toISO = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const mondayOf = (d = new Date()) => {
  const x = new Date(d); const day = x.getDay() || 7; x.setDate(x.getDate() - day + 1); x.setHours(0,0,0,0); return x;
};
const fmtShortDate = (iso) => { try { return new Date(iso + "T12:00:00").toLocaleDateString("es-MX", { day: "2-digit", month: "short" }); } catch { return iso; } };

export default function PaymentsReceivedReport() {
  const navigate = useNavigate();
  const today = useMemo(() => new Date(), []);
  const [from, setFrom] = useState(toISO(today));   // default: solo hoy
  const [to, setTo] = useState(toISO(today));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // "all" | "same_day" | "older"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/reports/payments-received", {
        params: { date_from: from, date_to: to },
      });
      setData(d);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const applyPreset = (preset) => {
    const t = new Date();
    let f, e;
    if (preset === "today") { f = t; e = t; }
    else if (preset === "yesterday") { const y = addDays(t, -1); f = y; e = y; }
    else if (preset === "this_week") { f = mondayOf(t); e = t; }
    else if (preset === "last_week") { f = mondayOf(addDays(t, -7)); e = addDays(f, 6); }
    else if (preset === "this_month") { f = new Date(t.getFullYear(), t.getMonth(), 1); e = t; }
    else if (preset === "last_month") { f = new Date(t.getFullYear(), t.getMonth() - 1, 1); e = new Date(t.getFullYear(), t.getMonth(), 0); }
    setFrom(toISO(f)); setTo(toISO(e));
  };

  const exportCSV = () => {
    if (!data || !data.payments.length) return;
    const headers = ["Fecha pago", "Folio", "Fecha nota", "Origen", "Cliente", "Vendedor", "Banco", "Monto", "Moneda", "Monto MXN"];
    const rows = data.payments.map((p) => [
      p.received_at, `#${String(p.folio).padStart(6, "0")}`, p.invoice_date,
      p.is_same_day ? "Venta del día" : "Cuenta por cobrar",
      p.client_name, p.seller_name, p.bank_name, p.amount, p.currency, p.amount_mxn,
    ]);
    const csv = [headers, ...rows].map((row) => row.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `pagos_recibidos_${from}_${to}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = data?.payments.filter((p) => {
    if (filter === "same_day") return p.is_same_day;
    if (filter === "older") return !p.is_same_day;
    return true;
  }) || [];

  const totals = data?.totals || { count: 0, amount_mxn: 0, same_day_count: 0, same_day_mxn: 0, older_count: 0, older_mxn: 0 };

  return (
    <section className="border border-zinc-200 bg-white" data-testid="payments-received-section">
      <div className="px-5 py-4 border-b border-zinc-100 flex flex-wrap items-center gap-3">
        <h3 className="font-heading text-lg font-semibold inline-flex items-center gap-2">
          <HandCoins size={20} weight="duotone" className="text-[#00873D]"/> Pagos recibidos
        </h3>
        <span className="text-[10px] font-mono-data uppercase tracking-widest text-zinc-400">Por fecha en que cayó el dinero</span>
        <div className="ml-auto flex flex-wrap gap-2 items-center">
          <div className="inline-flex border border-zinc-200 rounded overflow-hidden text-xs">
            {[["today", "Hoy"], ["yesterday", "Ayer"], ["this_week", "Esta semana"], ["last_week", "Semana pasada"], ["this_month", "Este mes"], ["last_month", "Mes pasado"]].map(([v, l]) => (
              <button key={v} onClick={() => applyPreset(v)} className="px-2.5 py-1.5 text-zinc-600 hover:bg-zinc-50" data-testid={`payments-preset-${v}`}>{l}</button>
            ))}
          </div>
          <div className="inline-flex items-center gap-1 border border-zinc-200 rounded px-2 py-1">
            <CalendarBlank size={14} className="text-zinc-500"/>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-7 px-1 border-0 text-xs w-32" data-testid="payments-date-from"/>
            <span className="text-zinc-400">→</span>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-7 px-1 border-0 text-xs w-32" data-testid="payments-date-to"/>
          </div>
          <Button variant="outline" size="sm" onClick={exportCSV} className="gap-1" data-testid="payments-export-csv"><Download size={14}/> CSV</Button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="payments-kpis">
        <button
          type="button"
          onClick={() => setFilter("all")}
          className={`text-left border p-4 rounded-sm transition-colors ${filter === "all" ? "border-[#002FA7]/60 bg-[#002FA7]/5" : "border-zinc-200 hover:bg-zinc-50"}`}
          data-testid="payments-filter-all"
        >
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500">Total recibido</div>
            <Money size={16} weight="duotone" className="text-[#002FA7]"/>
          </div>
          <div className="numeric text-2xl font-heading font-bold text-[#002FA7] mt-1">MX${fmt(totals.amount_mxn)}</div>
          <div className="text-[10px] text-zinc-500 mt-0.5 numeric">{totals.count} {totals.count === 1 ? "pago" : "pagos"}</div>
        </button>
        <button
          type="button"
          onClick={() => setFilter("same_day")}
          className={`text-left border p-4 rounded-sm transition-colors ${filter === "same_day" ? "border-[#00873D]/60 bg-[#00873D]/5" : "border-zinc-200 hover:bg-zinc-50"}`}
          data-testid="payments-filter-same-day"
          title="Pagos que se hicieron el mismo día que se facturó la venta"
        >
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500">Ventas del día</div>
            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded" style={{ background: "#00873D20", color: "#00873D" }}>DEL DÍA</span>
          </div>
          <div className="numeric text-2xl font-heading font-bold text-[#00873D] mt-1">MX${fmt(totals.same_day_mxn)}</div>
          <div className="text-[10px] text-zinc-500 mt-0.5 numeric">{totals.same_day_count} {totals.same_day_count === 1 ? "pago" : "pagos"}</div>
        </button>
        <button
          type="button"
          onClick={() => setFilter("older")}
          className={`text-left border p-4 rounded-sm transition-colors ${filter === "older" ? "border-[#FFC000]/60 bg-[#FFC000]/5" : "border-zinc-200 hover:bg-zinc-50"}`}
          data-testid="payments-filter-older"
          title="Pagos de notas anteriores que cayeron en este rango (cuentas por cobrar liquidadas)"
        >
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500">Cuentas por cobrar cobradas</div>
            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded" style={{ background: "#FFC00025", color: "#B58900" }}>ANTERIOR</span>
          </div>
          <div className="numeric text-2xl font-heading font-bold text-[#B58900] mt-1">MX${fmt(totals.older_mxn)}</div>
          <div className="text-[10px] text-zinc-500 mt-0.5 numeric">{totals.older_count} {totals.older_count === 1 ? "pago" : "pagos"}</div>
        </button>
      </div>

      {/* By-bank breakdown */}
      {data?.by_bank?.length > 0 && (
        <div className="px-5 pb-3">
          <div className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500 mb-1.5">Desglose por banco</div>
          <div className="flex flex-wrap gap-1.5" data-testid="payments-by-bank">
            {data.by_bank.map((b) => (
              <span key={b.bank_id} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border border-zinc-200 bg-zinc-50">
                <b className="text-zinc-800">{b.bank_name}</b>
                <span className="numeric text-[#00873D] font-semibold">MX${fmt(b.amount_mxn)}</span>
                <span className="text-zinc-400 numeric">·{b.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Payments table */}
      <div className="border-t border-zinc-100">
        {loading ? (
          <div className="p-8 text-center text-zinc-400 text-sm">Cargando pagos…</div>
        ) : filtered.length === 0 ? (
          <div className="dotted-bg py-10 text-center text-zinc-400 text-sm">Sin pagos en este rango</div>
        ) : (
          <table className="w-full text-sm" data-testid="payments-table">
            <thead className="bg-zinc-50 text-[10px] uppercase text-zinc-500">
              <tr>
                <th className="text-left px-4 py-2">Fecha pago</th>
                <th className="text-left px-4 py-2">Folio</th>
                <th className="text-left px-4 py-2">Origen</th>
                <th className="text-left px-4 py-2">Cliente</th>
                <th className="text-left px-4 py-2">Vendedor</th>
                <th className="text-left px-4 py-2">Banco</th>
                <th className="text-right px-4 py-2">Monto</th>
                <th className="text-right px-4 py-2">Monto MXN</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.payment_id} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" onClick={() => navigate(`/sales?open=${p.sale_id}`)} data-testid={`payment-row-${p.payment_id}`}>
                  <td className="px-4 py-2 text-xs">
                    <div className="font-mono-data font-semibold text-zinc-800">{fmtShortDate(p.received_at)}</div>
                    <div className="text-[10px] text-zinc-400 numeric">{new Date(p.received_at_full).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</div>
                  </td>
                  <td className="px-4 py-2 font-mono-data font-semibold text-[#002FA7] hover:underline">
                    #{String(p.folio).padStart(6, "0")} <ArrowSquareOut size={10} className="inline"/>
                    <div className="text-[10px] text-zinc-400 font-normal">nota {fmtShortDate(p.invoice_date)}</div>
                  </td>
                  <td className="px-4 py-2">
                    {p.is_same_day ? (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: "#00873D20", color: "#00873D" }}>DEL DÍA</span>
                    ) : (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded inline-flex items-center gap-1" style={{ background: "#FFC00025", color: "#B58900" }} title={`Nota del ${p.invoice_date} cobrada el ${p.received_at}`}>
                        <Clock size={9} weight="bold"/> ANTERIOR
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-zinc-800">{p.client_name}</td>
                  <td className="px-4 py-2 text-zinc-600 text-xs">{p.seller_name}</td>
                  <td className="px-4 py-2 text-zinc-600 text-xs">{p.bank_name}</td>
                  <td className="px-4 py-2 text-right numeric font-semibold">
                    {p.currency === "USD" ? "$" : "MX$"}{fmt(p.amount)}
                  </td>
                  <td className="px-4 py-2 text-right numeric font-bold text-[#00873D]">
                    MX${fmt(p.amount_mxn)}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-zinc-300 bg-zinc-50 font-semibold">
                <td colSpan={6} className="px-4 py-2 text-right text-xs uppercase text-zinc-600">Total del rango</td>
                <td className="px-4 py-2 text-right numeric text-[10px] text-zinc-500">{filtered.length} pagos</td>
                <td className="px-4 py-2 text-right numeric text-[#00873D] font-bold">
                  MX${fmt(filtered.reduce((sum, p) => sum + p.amount_mxn, 0))}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
